import React, { useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { Box } from '@chakra-ui/react';

// Quill's built-in icon set uses the same "</>" glyph for both inline `code`
// and block-level `code-block`, making the two toolbar buttons visually
// indistinguishable. Give inline code its own icon so users can tell them apart.
const icons = Quill.import('ui/icons');
icons.code = '<svg viewBox="0 0 18 18"><rect class="ql-fill" height="4" width="4" x="3" y="7" rx="1"></rect><rect class="ql-fill" height="4" width="4" x="11" y="7" rx="1"></rect><line class="ql-stroke" x1="9" x2="9" y1="7" y2="11"/></svg>';

const TOOLBAR_OPTIONS = [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike', 'code'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['link', 'blockquote', 'code-block'],
    ['clean'],
];

const RichTextEditor = ({ value, onChange, placeholder = 'Write step instructions here...' }) => {
    const editorRef = useRef(null);
    const quillRef = useRef(null);
    const isInternalChange = useRef(false);

    useEffect(() => {
        if (editorRef.current && !quillRef.current) {
            quillRef.current = new Quill(editorRef.current, {
                theme: 'snow',
                placeholder,
                modules: {
                    toolbar: {
                        container: TOOLBAR_OPTIONS,
                        handlers: {
                            // Quill's block formats are mutually exclusive — applying `list`
                            // to a heading line replaces the header blot outright, so a
                            // numbered "Step 1" heading silently flattens to body text.
                            // Re-apply the heading's visual weight as inline formatting
                            // (bold + size) so numbering a heading doesn't erase it.
                            list(value) {
                                const quill = this.quill;
                                const range = quill.getSelection();
                                if (!range) return;
                                const formats = quill.getFormat(range);

                                // Capture each affected line's header level and full text
                                // span *before* toggling list — formatLine() below replaces
                                // the header blot outright (Quill block formats are mutually
                                // exclusive), so headerLevel is unrecoverable afterwards. The
                                // captured (index, length) stay valid post-toggle since
                                // swapping the block type doesn't shift character offsets.
                                const lines = quill.getLines(range.index, Math.max(range.length, 1));
                                const headerSpans = lines
                                    .map(line => ({
                                        index: quill.getIndex(line),
                                        length: line.length() - 1,
                                        headerLevel: line.formats().header,
                                    }))
                                    .filter(span => span.headerLevel && span.length > 0);

                                if (value === 'check') {
                                    if (formats.list === 'checked' || formats.list === 'unchecked') {
                                        quill.format('list', false, Quill.sources.USER);
                                    } else {
                                        quill.format('list', 'unchecked', Quill.sources.USER);
                                    }
                                } else {
                                    quill.format('list', value, Quill.sources.USER);
                                }

                                if (value) {
                                    const sizeByLevel = { 1: 'huge', 2: 'large', 3: 'large' };
                                    headerSpans.forEach(({ index, length, headerLevel }) => {
                                        quill.formatText(index, length, 'bold', true, Quill.sources.USER);
                                        quill.formatText(index, length, 'size', sizeByLevel[headerLevel] || 'large', Quill.sources.USER);
                                    });
                                }
                            },
                            // Pasting code/markup from a syntax-highlighted source (a code
                            // viewer, IDE web export, etc.) can land as real rich-formatted
                            // content — Quill's code-block only allows plain text internally,
                            // so converting that selection in place can drop the literal
                            // "<tag>" characters along with the formatting. Re-extract the
                            // plain text first and reinsert it fresh as a code block so only
                            // escapeText() ever decides how those characters render.
                            'code-block'(value) {
                                const quill = this.quill;
                                const range = quill.getSelection();
                                if (!range) return;
                                if (value && range.length > 0) {
                                    // A selection typically stops right before the last
                                    // line's own trailing "\n" rather than past it. Reinserting
                                    // just that substring leaves the last line's line break
                                    // untouched, so it never picks up the code-block attribute
                                    // and that line renders as normal text. Extend the range to
                                    // the end of the last touched line so its break is included
                                    // in the delete/reinsert and gets formatted too.
                                    const [lastLine] = quill.getLine(range.index + range.length);
                                    const lineEnd = quill.getIndex(lastLine) + lastLine.length();
                                    const length = Math.max(range.length, lineEnd - range.index);

                                    const text = quill.getText(range.index, length);
                                    quill.deleteText(range.index, length, Quill.sources.USER);
                                    quill.insertText(range.index, text, { 'code-block': true }, Quill.sources.USER);
                                    quill.setSelection(range.index, text.length, Quill.sources.SILENT);
                                } else {
                                    quill.format('code-block', value, Quill.sources.USER);
                                }
                            },
                        },
                    },
                    keyboard: {
                        bindings: {
                            // Quill carries whatever format sits just left of a collapsed
                            // cursor into anything typed next — so landing at the end of
                            // an inline `code` run and continuing to type keeps producing
                            // code, with no keyboard-only way out short of reopening the
                            // toolbar. Right Arrow at that exact boundary is the natural
                            // "step outside" gesture, so once it would move past the last
                            // `code` character, drop `code` from what applies going
                            // forward — same effect as toggling the toolbar button.
                            // Anywhere else inside the run, let the arrow move normally.
                            'exit-inline-code': {
                                key: 'ArrowRight',
                                collapsed: true,
                                format: ['code'],
                                handler(range) {
                                    const quill = this.quill;
                                    // getFormat(index, 0) — a zero-length, boundary query — falls
                                    // back to "inherit from the left", which is exactly the
                                    // carry-forward behavior we're trying to escape, so it always
                                    // reports `code: true` here. Querying the real character the
                                    // cursor is about to step over (length 1, starting exactly at
                                    // the current position) reports its actual format instead.
                                    const stillInCode = !!quill.getFormat(range.index, 1).code;
                                    if (stillInCode) return true;
                                    const nextIndex = Math.min(range.index + 1, quill.getLength() - 1);
                                    quill.setSelection(nextIndex, 0, Quill.sources.USER);
                                    quill.format('code', false, Quill.sources.USER);
                                    return false;
                                },
                            },
                        },
                    },
                },
            });

            const inlineCodeButton = editorRef.current.parentElement.querySelector('.ql-toolbar button.ql-code');
            if (inlineCodeButton) inlineCodeButton.setAttribute('title', 'Inline Code');
            const codeBlockButton = editorRef.current.parentElement.querySelector('.ql-toolbar button.ql-code-block');
            if (codeBlockButton) codeBlockButton.setAttribute('title', 'Code Block');

            quillRef.current.on('text-change', () => {
                isInternalChange.current = true;
                if (onChange) onChange(quillRef.current.getSemanticHTML());
                isInternalChange.current = false;
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const quill = quillRef.current;
        if (!quill || isInternalChange.current) return;

        const currentHtml = quill.getSemanticHTML();
        if ((value || '') !== currentHtml) {
            const selection = quill.getSelection();
            quill.clipboard.dangerouslyPasteHTML(value || '');
            if (selection) {
                const length = quill.getLength();
                quill.setSelection(Math.min(selection.index, length - 1), selection.length);
            }
        }
    }, [value]);

    return (
        <Box
            sx={{
                '.ql-container': {
                    minHeight: '220px',
                    fontSize: '14px',
                    borderBottomLeftRadius: '6px',
                    borderBottomRightRadius: '6px',
                },
                '.ql-editor': {
                    minHeight: '220px',
                },
                '.ql-editor .ql-code-block-container': {
                    fontFamily: '"Courier New", Courier, monospace',
                    fontSize: '12.5px',
                },
                '.ql-editor code': {
                    backgroundColor: 'gray.100',
                    color: 'pink.600',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    fontFamily: '"Courier New", Courier, monospace',
                    fontSize: '13px',
                },
                // A heading turned into a numbered list item keeps its size via inline
                // bold/size formatting (see the `list` toolbar handler above), but the
                // marker itself stays at the line's base font-size and aligns to the
                // baseline by default — against a much taller run of text that sinks it
                // toward the bottom of the line. Middle-align it instead so it stays
                // readable next to text of any size.
                '.ql-editor li > .ql-ui:before': {
                    verticalAlign: 'middle',
                },
                // Quill's own CSS resets list numbering on every plain paragraph, not just
                // headings (counter-set on `.ql-editor p`) — so a line of ordinary text
                // between two list items silently restarts the count in the live editor,
                // even though the saved/exported output keeps the sequence going. Numbering
                // should only start a new section at a heading, matching what gets saved.
                '.ql-editor p': {
                    counterSet: 'none',
                },
                '.ql-toolbar': {
                    borderTopLeftRadius: '6px',
                    borderTopRightRadius: '6px',
                },
            }}
        >
            <div ref={editorRef} />
        </Box>
    );
};

export default RichTextEditor;
